/** Filter parse/params for SmswiserCampaignStatsTab. For Media Devoted | Created By Ciro Autiero */
import {
  FILTER_FIELDS,
  FILTER_FIELDS_GATEWAYS,
  FILTER_FIELDS_LISTS,
  FILTER_FIELDS_OFFERS,
  FILTER_FIELDS_PATHS,
  FILTER_FIELDS_LANDERS,
  FILTER_FIELDS_PHONE,
  FILTER_FIELDS_SOURCES,
  FILTER_FIELDS_TEXTS,
  NUMERIC_OPERATORS,
} from '../constants';

export interface FilterConfig {
  field: string;
  operator: string;
  value: string;
}
export interface FiltersState {
  filters: FilterConfig[];
  active: boolean;
}

const VALID_FILTER_FIELDS = new Set(
  [
    FILTER_FIELDS,
    FILTER_FIELDS_LISTS,
    FILTER_FIELDS_GATEWAYS,
    FILTER_FIELDS_SOURCES,
    FILTER_FIELDS_TEXTS,
    FILTER_FIELDS_PATHS,
    FILTER_FIELDS_LANDERS,
    FILTER_FIELDS_OFFERS,
    FILTER_FIELDS_PHONE,
  ].flatMap((arr) => arr.map((f) => f.key)),
);
const VALID_FILTER_OPS = new Set(NUMERIC_OPERATORS.map((o) => o.op));
type FilterOp = (typeof NUMERIC_OPERATORS)[number]['op'];
function toFilterOp(s: string): FilterOp {
  return (VALID_FILTER_OPS.has(s as FilterOp) ? s : '>=') as FilterOp;
}

function stripControlChars(s: string): string {
  return s.replace(/[^\t\n\r\x20-\x7e]/g, '');
}

const MAX_FILTER_VALUE_LEN = 200;
const NUMERIC_RANGE = { min: -1e12, max: 1e12 };

function isTextField(field: string): boolean {
  return ['leadList', 'list', 'tags', 'staticTag', 'gateway', 'path', 'lander', 'offer', 'source', 'text', 'phone'].includes(field);
}

export function parseFilterFromParams(
  fieldParam: string | null,
  opParam: string | null,
  valueParam: string | null,
  defaults: FilterConfig,
): FilterConfig {
  const field = VALID_FILTER_FIELDS.has(fieldParam ?? '') ? (fieldParam as string) : defaults.field;
  const isText = isTextField(field);
  const op: FilterOp = isText ? '=' : toFilterOp(opParam ?? defaults.operator);

  let value = (valueParam ?? '').trim();
  if (value.length > MAX_FILTER_VALUE_LEN) value = value.slice(0, MAX_FILTER_VALUE_LEN);
  value = stripControlChars(value);

  if (isText) return { field, operator: '=', value };
  if (!value) {
    const fallback = (defaults.value ?? '').trim();
    if (!fallback) return { field, operator: op, value: '' };
    const num = parseFloat(fallback);
    if (!Number.isFinite(num)) return { field, operator: op, value: '' };
    const clamped = Math.max(NUMERIC_RANGE.min, Math.min(NUMERIC_RANGE.max, num));
    return { field, operator: op, value: String(clamped) };
  }

  const num = parseFloat(value);
  if (!Number.isFinite(num)) return { field, operator: op, value: '' };
  const clamped = Math.max(NUMERIC_RANGE.min, Math.min(NUMERIC_RANGE.max, num));
  return { field, operator: op, value: String(clamped) };
}

export function filterToParams(config: FilterConfig): {
  filterField?: string;
  filterOp?: string;
  filterValue?: string;
} {
  const { field, operator, value } = config;
  if (!VALID_FILTER_FIELDS.has(field)) return {};
  const op: FilterOp = isTextField(field) ? '=' : toFilterOp(operator);
  const trimmed = value.trim();
  const base = { filterField: field, filterOp: op };
  if (!trimmed) return base;
  const safeValue = stripControlChars(trimmed.slice(0, MAX_FILTER_VALUE_LEN));
  return { ...base, filterValue: safeValue };
}

const MAX_FILTERS = 10;

function getFilterParams(
  get: (k: string) => string | null,
  idx: number,
): { field: string | null; op: string | null; value: string | null } {
  const prefix = idx === 0 ? '' : String(idx);
  return {
    field: get(`filter${prefix}Field`) ?? (idx === 0 ? get('filterField') : null),
    op: get(`filter${prefix}Op`) ?? (idx === 0 ? get('filterOp') : null),
    value: get(`filter${prefix}Value`) ?? (idx === 0 ? get('filterValue') : null),
  };
}

export function parseFiltersFromParams(
  getParam: (k: string) => string | null,
  defaultFilter: FilterConfig,
): FiltersState {
  const activeParam = getParam('filterActive');
  const active = activeParam !== '0';

  const filters: FilterConfig[] = [];
  for (let i = 0; i < MAX_FILTERS; i++) {
    const { field, op, value } = getFilterParams(getParam, i);
    const hasAny = field != null || op != null || value != null;
    if (!hasAny && i > 0) break;
    const parsed = parseFilterFromParams(field, op, value, defaultFilter);
    filters.push(parsed);
  }
  if (filters.length === 0) filters.push({ ...defaultFilter });
  return { filters, active };
}

export function filtersToParams(state: FiltersState): Record<string, string> {
  const out: Record<string, string> = {};
  out.filterActive = state.active ? '1' : '0';
  state.filters.forEach((f, i) => {
    const p = filterToParams(f);
    const prefix = i === 0 ? '' : String(i);
    if (p.filterField) out[`filter${prefix}Field`] = p.filterField;
    if (p.filterOp) out[`filter${prefix}Op`] = p.filterOp;
    if (p.filterValue != null) out[`filter${prefix}Value`] = p.filterValue;
  });
  return out;
}
